<template>
  <div>
    <page-title :heading=heading :subheading=subheading :icon=icon></page-title>
    <v-container>
      <v-layout style="padding-bottom: 10px;">
        <v-flex class="ml-2" xs1>
          <v-text-field v-model="year" :label="$t('annualExpensesReport.year')" :rules="rules.yearRules" mask="####" class="fixed-height-textfield" outline></v-text-field>
        </v-flex>
        <v-flex class="ml-2" xs2>
          <v-select v-model="userCompanySelected" :items="userCompany" :label="$t('expenseDetailEmployee.employee')" item-text="name" item-value="id" class="fixed-height" solo>
            <template v-slot:selection="{ item }">
              <div class="ellipsis-2-lines" style="-webkit-box-orient: vertical !important;" :title="item.name">
                {{ item.name }}
              </div>
            </template>
          </v-select>
        </v-flex>
        <v-flex class="ml-2" xs2>
          <v-select v-model="selectedCategory" :items="categories" :rules="rules.required" :label="$t('general.categories')" class="fixed-height" item-value="id" item-text="name" solo>
            <template v-slot:selection="{ item }">
              <div class="ellipsis-2-lines" style="-webkit-box-orient: vertical !important;" :title="item.name">
                {{ item.name }}
              </div>
            </template>
          </v-select>
        </v-flex>
        <v-flex class="ml-2" xs3>
          <v-select v-model="selectedProject" :items="projects" :label="$t('general.projects')" class="fixed-height" item-value="id" item-text="name" solo>
            <template slot="item" slot-scope="data">
              {{ data.item.name }} - {{ data.item.status === 1 ? $t('projects.stat.active') : $t('projects.stat.inactive')}}
            </template>
            <template v-slot:selection="{ item }">
              <div class="ellipsis-2-lines" style="-webkit-box-orient: vertical !important;" :title="item.name">
                {{ item.name }}
              </div>
            </template>
          </v-select>
        </v-flex>
        <v-flex class="ml-2" xs3>
          <v-select v-model="selectedArea" :items="areas" :label="$t('general.areas')" class="fixed-height" item-value="id" item-text="name" solo></v-select>
        </v-flex>
        <flex class="ml-2" xs1>
          <v-btn @click="search" :loading="loading" :disabled="disabled" color="primary" round depressed>
            <v-icon>search</v-icon>
            {{ $t('general.search') }}
          </v-btn>
        </flex>
      </v-layout>
      <v-layout  class='text-sm-right'>
        <v-flex>
          <v-btn v-if="show && expenses.employed.length !== 0" @click="downloadXls()" :loading="loadingDownload" color='primary' round outline>
            {{$t('expenseDetailEmployee.download')}}
            <v-icon right>cloud_download</v-icon>
          </v-btn>
          <v-btn v-if="show && expenses.length != 0" @click="downloadZipDialog = true" color="primary" round outline>
            {{$t('general.downloadExpensesFiles')}}
            <v-icon right>get_app</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
     <v-layout v-if="loadingItems">
        <v-flex>
          <v-progress-circular
            indeterminate
            color="primary"
          ></v-progress-circular>
          <span>{{$t('general.loadingText')}}</span>
        </v-flex>
      </v-layout>
      <v-layout v-if="!loadingItems">
        <v-flex>
        <v-data-table
        :headers="headers"
        :items="expenses.employed"
        class="elevation-1"
        :loading="loadingItems"
        no-data-text=""
        :rows-per-page-items="[10,15,25]"
        :pagination.sync="pagination">

          <template v-slot:no-data v-if="expenses.employed.length > 0">
            <center class="subtitle font-weight-light">{{$t('general.noDataText')}}</center>
          </template>

        <template v-slot:headers='props'>
          <tr class="bg-heavy-rain">
              <th
                v-for="header in props.headers"
                :key="header.text"
                :class="['column', header.sortable ? 'sortable' : '', pagination.descending ? 'desc' : 'asc', header.value === pagination.sortBy ? 'active' : '']"
                @click="changeSort(header)"
              >
              <h2 class="font-weight-black primary--text mb-0">
                  <v-icon small class="primary--text" v-if="header.sortable">arrow_upward</v-icon>
                  {{header.text}}
              </h2>
              </th>
          </tr>
        </template>
        <template v-slot:items="props">
          <td class="text-xs-center">{{ props.item.name }}</td>
          <td class="text-xs-center">{{ props.item.months[0].sumtotal ? props.item.months[0].sumtotal : 0.0 | formatCurrency }}</td>
          <td class="text-xs-center">{{ props.item.months[1].sumtotal ? props.item.months[1].sumtotal : 0.0 | formatCurrency }}</td>
          <td class="text-xs-center">{{ props.item.months[2].sumtotal ? props.item.months[2].sumtotal : 0.0 | formatCurrency }}</td>
          <td class="text-xs-center">{{ props.item.months[3].sumtotal ? props.item.months[3].sumtotal : 0.0 | formatCurrency }}</td>
          <td class="text-xs-center">{{ props.item.months[4].sumtotal ? props.item.months[4].sumtotal : 0.0 | formatCurrency }}</td>
          <td class="text-xs-center">{{ props.item.months[5].sumtotal ? props.item.months[5].sumtotal : 0.0 | formatCurrency }}</td>
          <td class="text-xs-center">{{ props.item.months[6].sumtotal ? props.item.months[6].sumtotal : 0.0 | formatCurrency }}</td>
          <td class="text-xs-center">{{ props.item.months[7].sumtotal ? props.item.months[7].sumtotal : 0.0 | formatCurrency }}</td>
          <td class="text-xs-center">{{ props.item.months[8].sumtotal ? props.item.months[8].sumtotal : 0.0 | formatCurrency }}</td>
          <td class="text-xs-center">{{ props.item.months[9].sumtotal ? props.item.months[9].sumtotal : 0.0 | formatCurrency }}</td>
          <td class="text-xs-center">{{ props.item.months[10].sumtotal ? props.item.months[10].sumtotal : 0.0 | formatCurrency }}</td>
          <td class="text-xs-center">{{ props.item.months[11].sumtotal ? props.item.months[11].sumtotal : 0.0 | formatCurrency }}</td>
          <td class="text-xs-center">{{ props.item.totalTip ? props.item.totalTip : 0.0 | formatCurrency }}</td>
          <td class="text-xs-center">{{ props.item.total ? (+props.item.total) + (props.item.totalTip ? +props.item.totalTip : +0) : 0.0 | formatCurrency }}</td>
        </template>
        <!-- <template v-slot:footer v-if="show && expenses.employed !== 0">
          <tr>
            <td class="text-center"><strong>{{$t('general.total')}}</strong></td>
            <td class="text-center"><strong>{{expenses.totales[0] | formatCurrency}}</strong></td>
            <td class="text-center"><strong>{{expenses.totales[1] | formatCurrency}}</strong></td>
            <td class="text-center"><strong>{{expenses.totales[2] | formatCurrency}}</strong></td>
            <td class="text-center"><strong>{{expenses.totales[3] | formatCurrency}}</strong></td>
            <td class="text-center"><strong>{{expenses.totales[4] | formatCurrency}}</strong></td>
            <td class="text-center"><strong>{{expenses.totales[5] | formatCurrency}}</strong></td>
            <td class="text-center"><strong>{{expenses.totales[6] | formatCurrency}}</strong></td>
            <td class="text-center"><strong>{{expenses.totales[7] | formatCurrency}}</strong></td>
            <td class="text-center"><strong>{{expenses.totales[8] | formatCurrency}}</strong></td>
            <td class="text-center"><strong>{{expenses.totales[9] | formatCurrency}}</strong></td>
            <td class="text-center"><strong>{{expenses.totales[10] | formatCurrency}}</strong></td>
            <td class="text-center"><strong>{{expenses.totales[11] | formatCurrency}}</strong></td>
            <td class="text-center"><strong>{{expenses.totales.total | formatCurrency}}</strong></td>
          </tr>
        </template> -->
      </v-data-table>
        </v-flex>
      </v-layout>
      <v-dialog
        v-model="downloadZipDialog"
        persistent
        max-width="600px"
      >
        <v-card>
          <v-toolbar flat>
            <h2 class="font-weight-black primary--text mb-0">{{$t('general.downloadExpensesFiles')}}</h2>
          </v-toolbar>
          <v-card-text>
            <v-container>
              <v-form
              ref="valDownload"
              lazy-validation
              >
              <v-layout wrap>
                <v-flex xs12>
                  <v-select
                    :items="zipsAvailables"
                    item-value="month"
                    v-model="selectedZip"
                    :label="$t('general.availableFiles')"
                    return-object
                    :rules="rules.required"
                    required
                  >
                    <template slot="selection" slot-scope="data">
                      ({{year + '-' + data.item.month}}) {{$t('general.expensesFiles')}}
                    </template>
                    <template slot="item" slot-scope="data">
                      ({{year + '-' + data.item.month}}) {{$t('general.expensesFiles')}}
                    </template>
                  </v-select>
                </v-flex>
              </v-layout>
              </v-form>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              flat
              round
              @click="closeDownloadDialog"
            >{{$t('general.close')}}</v-btn>
            <v-btn
              color="primary"
              depressed
              round
              @click="downloadFiles()"
              :loading="loading"
              :disabled="loading"
            >{{$t('general.download')}}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
export default {
  name: 'annualExpensesByEmployedReportComponent',
  data () {
    return {
      expenses: {
        employed: [],
        totales: {
          '0': 0,
          '1': 0,
          '2': 0,
          '3': 0,
          '4': 0,
          '5': 0,
          '6': 0,
          '7': 0,
          '8': 0,
          '9': 0,
          '10': 0,
          '11': 0,
          'total': 0
        }
      },
      year: new Date().getUTCFullYear(),
      pagination: {
        descending: false,
        page: 0,
        rowsPerPage: 15, // -1 for All",
        totalItems: 0
      },
      dialog: false,
      allUsers: true,
      areas: [],
      status: [],
      userCompany: [],
      categories: [],
      projects: [],
      switchCategories: true,
      selectedCategory: null,
      switchProjects: true,
      selectedProject: null,
      switchAreas: true,
      selectedArea: null,
      loading: false,
      loadingItems: true,
      disabled: false,
      areasSelected: null,
      statusSelected: null,
      userCompanySelected: null,
      projectsSelected: null,
      type: 'json',
      urlQuery: '',
      show: false,
      link: '',
      userLogged: {},
      valid: true,
      downloadZipDialog: false,
      totalMonths: [],
      detailedData: [],
      expensesItems: null,
      selectedZip: null,
      valDownload: true,
      loadingDownload: false
    }
  },
  created () {
    this.getLogged()
    // this.initialize()
  },
  methods: {
    getLogged () {
      if (this.$store.state.userLogged.company) {
        this.userLogged = this.$store.state.userLogged
      }
    },
    initialize () {
      return this.$emit('initialize', {company_id: this.$store.state.userLogged.company[0].id, context: this})
    },
    get () {
      return this.$emit('get', {context: this})
    },
    closed () {
      this.dialog = false
    },
    getExpenses () {
      this.search()
      return this.expenses.employed
    },
    search () {
      this.type = 'json'
        this.loadingItems = true
        this.loading = true
        this.disabled = true
        this.get()
    },
    downloadReport () {
      this.type = 'download'
      this.$emit('download', {context: this})
    },
    downloadXls () {
      this.loadingDownload = true
      this.downloadReport()
    },
    changeSort (header) {
      if (header.sortable) {
        if (this.pagination.sortBy === header.value) {
          this.pagination.descending = !this.pagination.descending
        } else {
          this.pagination.descending = false
        }
        this.pagination.sortBy = header.value
      } else {
        this.pagination.sortBy = ''
      }
    },
    closeDownloadDialog () {
      this.downloadZipDialog = false
      this.selectedZip = null
    },
    downloadFiles () {
      if (this.$refs.valDownload.validate()) {
        this.$emit('downloadFiles', {
          context: this,
          expensesItems: this.selectedZip,
          date: this.year
        })
        this.loading = true
      }
    }
  },
  computed: {
    heading () {
      return this.$t('annualExpensesByEmployedReport.name')
    },
    subheading () {
      return this.year.toString()
    },
    icon () {
      return 'far fa-file-alt fa-fw icon-color'
    },
    options () {
      return {
        color: 'primary',
        elevation: 'elevation-2',
        medium: true,
        outline: true,
        round: true,
        rigth: true
      }
    },
    headers () {
      return [
        {
          text: this.$t('general.user'),
          align: 'left',
          sortable: true,
          value: 'name'
        },
        {
          text: this.$t('general.months.jan'),
          align: 'left',
          sortable: true,
          value: 'months[0].sumtotal'
        },
        {
          text: this.$t('general.months.feb'),
          align: 'left',
          sortable: true,
          value: 'months[1].sumtotal'
        },
        {
          text: this.$t('general.months.mar'),
          align: 'left',
          sortable: true,
          value: 'months[2].sumtotal'
        },
        {
          text: this.$t('general.months.apr'),
          align: 'left',
          sortable: true,
          value: 'months[3].sumtotal'
        },
        {
          text: this.$t('general.months.may'),
          align: 'left',
          sortable: true,
          value: 'months[4].sumtotal'
        },
        {
          text: this.$t('general.months.jun'),
          align: 'left',
          sortable: true,
          value: 'months[5].sumtotal'
        },
        {
          text: this.$t('general.months.jul'),
          align: 'left',
          sortable: true,
          value: 'months[6].sumtotal'
        },
        {
          text: this.$t('general.months.aug'),
          align: 'left',
          sortable: true,
          value: 'months[7].sumtotal'
        },
        {
          text: this.$t('general.months.sep'),
          align: 'left',
          sortable: true,
          value: 'months[8].sumtotal'
        },
        {
          text: this.$t('general.months.oct'),
          align: 'left',
          sortable: true,
          value: 'months[9].sumtotal'
        },
        {
          text: this.$t('general.months.nov'),
          align: 'left',
          sortable: true,
          value: 'months[10].sumtotal'
        },
        {
          text: this.$t('general.months.dec'),
          align: 'left',
          sortable: true,
          value: 'months[11].sumtotal'
        },
        {
          text: this.$t('expenses.tip'),
          align: 'left',
          sortable: false,
          value: 'totalTip'
        },
        {
          text: this.$t('general.total'),
          align: 'left',
          sortable: true,
          value: 'total'
        }
      ]
    },
    rules () {
      return this.$validationRules()
    },
    zipsAvailables () {
      if (this.expensesItems && this.expensesItems.length > 0) {
        return this.expensesItems
      }
    }
  },
  watch: {
    '$store.state.userLogged' () {
      if (this.$store.state.userLogged.company) {
        this.getLogged()
      }
    },
    userLogged () {
      if (this.userLogged.company) {
        this.initialize()
        this.search()
      }
    },
    allUsers () {
      if (this.allUsers) {
        this.userCompanySelected = null
      }
    },
    switchCategories () {
      if (this.switchCategories) {
        this.selectedCategory = null
      }
    },
    switchAreas () {
      if (this.switchAreas) {
        this.selectedArea = null
      }
    },
    switchProjects () {
      if (this.switchProjects) {
        this.selectedProject = null
      }
    },
    areas () {
      if (this.areas !== undefined && this.areas.find(x => x.id === -1) === undefined) {
        let objDefault = { id: -1, name: this.$t('expenseDetailEmployee.allAreas') }
        this.selectedArea = -1
        this.areas.unshift(objDefault)
      }
    },
    userCompany () {
      if (this.userCompany !== undefined && this.userCompany.find(x => x.id === -1) === undefined) {
        let objDefault = { id: -1, name: this.$t('expenseDetailEmployee.allEmployees') }
        this.userCompanySelected = -1
        this.userCompany.unshift(objDefault)
      }
    },
    projects () {
      if (this.projects !== undefined && this.projects.find(x => x.id === -1) === undefined) {
        let objDefault = { id: -1, name: this.$t('expenseDetailEmployee.allProjects') }
        this.selectedProject = -1
        this.projects.unshift(objDefault)
      }
    },
    categories () {
      if (this.categories !== undefined && this.categories.find(x => x.id === -1) === undefined) {
        let objDefault = { id: -1, name: this.$t('expenseDetailEmployee.allCategories') }
        this.selectedCategory = -1
        this.categories.unshift(objDefault)
      }
    }
  }
}
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/components/annualExpensesByEmployedReport.component.vue